import React, { useState } from 'react';
import { Alert, Button, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import PropTypes from 'prop-types';
import FalconCloseButton from 'components/common/FalconCloseButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { useToast } from 'context/providers/ToastProvider';

const ModalRegistrarPagoAddProduct = ({
    show,
    onHide,
    orden,
    idOrden,
    formaPago,
    addProductContext,
    token,
    setToken,
    onSuccess
}) => {
    const { showSuccessToast, showErrorToast } = useToast();
    const [procesando, setProcesando] = useState(false);
    const [form, setForm] = useState({
        idCatFormaPago: '',
        vReferencia: '',
        dFechaPago: new Date().toISOString().split('T')[0],
        vArchivo: null,
        vNombreDocumento: '',
        vTipoDocumento: '',
        vTamano: 0
    });

    const validar = () => {
        if (!form.idCatFormaPago) {
            showErrorToast('Es necesario que elija la forma de pago');
            return false;
        }
        if (!form.dFechaPago) {
            showErrorToast('Debes seleccionar la fecha de pago');
            return false;
        }
        if (!form.vReferencia.trim()) {
            showErrorToast('Es necesario ingresar una referencia o folio');
            return false;
        }
        if (!form.vArchivo) {
            showErrorToast('Es obligatorio subir un comprobante de pago');
            return false;
        }
        if (!orden.dSaldoInsoluto || parseFloat(orden.dSaldoInsoluto) <= 0) {
            showErrorToast('El monto a pagar no puede ser cero');
            return false;
        }
        return true;
    };

    const onSubmit = async () => {
        if (!validar()) return;
        setProcesando(true);

        let archivoBase64 = null;
        let nombreArchivo = null;
        let tipoArchivo = null;

        if (form.vArchivo) {
            const file = form.vArchivo;
            nombreArchivo = file.name;
            tipoArchivo = file.type;

            const reader = new FileReader();
            archivoBase64 = await new Promise((resolve, reject) => {
                reader.onload = () => {
                    const base64 = reader.result.split(',')[1];
                    resolve(base64);
                };
                reader.onerror = reject;
                reader.readAsDataURL(file);
            });
        }

        const payload = {
            IdSuscripcion: addProductContext.idSuscripcion,
            IdOrden: parseInt(idOrden),
            IdSuscripcionDetalle: addProductContext.idSuscripcionDetalle,
            DImportePagado: parseFloat(orden.dSaldoInsoluto),
            VMoneda: orden.vMoneda,
            DTipoCambio: orden.dTipoCambio ?? 1,
            IdCatFormaPago: parseInt(form.idCatFormaPago),
            VReferencia: form.vReferencia,
            VJsonDetallesPago: '{}',
            DFechaPago: form.dFechaPago,
            VArchivoBase64: archivoBase64,
            VNombreArchivo: nombreArchivo,
            VTipoArchivo: tipoArchivo
        };

        const response = await fetch('/api/Suscripciones/RegistrarPagoAddProduct', {
            method: 'POST',
            headers: {
                'Authorization': 'Bearer ' + token,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
        }).catch((error) => {
            showErrorToast('Hubo un problema con la petición: ' + error);
            setProcesando(false);
            return null;
        });

        if (!response) return;

        if (response.ok) {
            const data = await response.json();
            if (data.iResponseType === 1) {
                setProcesando(false);
                setForm({ idCatFormaPago: '', vReferencia: '', dFechaPago: '' });
                showSuccessToast('Pago registrado correctamente.');
                onSuccess();
            } else {
                setProcesando(false);
                showErrorToast(data.vMessage || 'Error al registrar el pago');
            }
        } else {
            setProcesando(false);
            showErrorToast(response.status + ' ' + response.statusText);
            if (response.status === 401) {
                setToken(null);
                location.href = '';
            }
        }
    };

    return (
        <Modal
            show={show}
            onHide={onHide}
            size="md"
            backdrop="static"
            aria-labelledby="contained-modal-title-vcenter"
            centered
        >
            <Modal.Header>
                <Modal.Title>Registrar Pago — Producto Adicional</Modal.Title>
                <FalconCloseButton className="btn btn-circle btn-sm transition-base p-0" onClick={onHide} />
            </Modal.Header>
            <Modal.Body>
                <Alert variant="warning" className="d-flex align-items-center fs--1 mb-3 py-2 border-warning">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="text-warning fs-0 me-2 flex-shrink-0" />
                    <div>
                        <strong>Aviso:</strong> Al registrar este pago, la orden quedará marcada como pagada y será <strong>bloqueada para edición</strong>.
                    </div>
                </Alert>

                <Row>
                    <Col md={12} className="text-center mb-3">
                        <div className="fs-2 fw-semi-bold">Total a Pagar:</div>
                        <div className="fs-4 fw-semi-bold">
                            <span className="text-danger">
                                ${parseFloat(orden.dSaldoInsoluto || 0).toLocaleString('es-MX', {
                                    maximumFractionDigits: 2,
                                    minimumFractionDigits: 2
                                })}
                            </span> {orden.vMoneda}
                        </div>
                        <hr />
                    </Col>
                </Row>

                <Form.Group className="mb-3">
                    <Form.Label>Monto del Pago</Form.Label>
                    <Form.Control
                        type="number"
                        size="md"
                        value={parseFloat(orden.dSaldoInsoluto || 0).toFixed(2)}
                        disabled
                    />
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>Forma de pago</Form.Label>
                    <Form.Select
                        value={form.idCatFormaPago}
                        onChange={(e) => setForm({ ...form, idCatFormaPago: e.target.value })}
                    >
                        <option value="">Selecciona forma de pago</option>
                        {formaPago.map((fp, index) => (
                            <option key={index} value={fp.idCatFormaPago}>{fp.vDescripcionFormaPago}</option>
                        ))}
                    </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>Fecha del Pago</Form.Label>
                    <Form.Control
                        type="date"
                        size="md"
                        value={form.dFechaPago}
                        onChange={(e) => setForm({ ...form, dFechaPago: e.target.value })}
                    />
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>Folio / Rastreo</Form.Label>
                    <Form.Control
                        type="text"
                        size="md"
                        value={form.vReferencia}
                        onChange={(e) => setForm({ ...form, vReferencia: e.target.value })}
                    />
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>Comprobante de pago</Form.Label>
                    <Form.Control
                        type="file"
                        size="sm"
                        required
                        onChange={(e) => setForm({
                            ...form,
                            vArchivo: e.target.files[0],
                            vNombreDocumento: e.target.files[0].name,
                            vTipoDocumento: e.target.files[0].type,
                            vTamano: e.target.files[0].size,
                        })}
                    />
                </Form.Group>
            </Modal.Body>

            <Modal.Footer>
                <Button onClick={onHide} variant="primary" disabled={procesando}>Cerrar</Button>
                {procesando ? (
                    <Button variant="success" disabled>
                        <Spinner as="span" animation="grow" size="sm" role="status" aria-hidden="true" />
                        &nbsp;Procesando...
                    </Button>
                ) : (
                    <Button type="button" onClick={onSubmit} variant="success">REGISTRAR PAGO</Button>
                )}
            </Modal.Footer>
        </Modal>
    );
};

ModalRegistrarPagoAddProduct.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    orden: PropTypes.object.isRequired,
    idOrden: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    formaPago: PropTypes.array.isRequired,
    addProductContext: PropTypes.object,
    token: PropTypes.string,
    setToken: PropTypes.func.isRequired,
    onSuccess: PropTypes.func.isRequired
};

export default ModalRegistrarPagoAddProduct;
